import { cn } from "@githits/shared";
import type { Reference } from "../data/types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "./ui/tooltip";

function formatRelativeTime(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 1) return "today";
  if (diffDays < 30) return `${diffDays}d ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths}mo ago`;
  const diffYears = Math.floor(diffMonths / 12);
  return `${diffYears}y ago`;
}

function TypeIcon({ type }: { type: string }) {
  if (type === "code") {
    return (
      <svg className="ghw-w-3 ghw-h-3 ghw-text-muted-foreground ghw-shrink-0" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M4.72 3.22a.75.75 0 0 1 1.06 1.06L2.56 7.5l3.22 3.22a.75.75 0 1 1-1.06 1.06l-3.75-3.75a.75.75 0 0 1 0-1.06l3.75-3.75Zm6.56 0a.75.75 0 1 0-1.06 1.06L13.44 7.5l-3.22 3.22a.75.75 0 1 0 1.06 1.06l3.75-3.75a.75.75 0 0 0 0-1.06l-3.75-3.75Z" />
      </svg>
    );
  }
  // doc / other
  return (
    <svg className="ghw-w-3 ghw-h-3 ghw-text-muted-foreground ghw-shrink-0" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M3.75 1.5a.25.25 0 0 0-.25.25v11.5c0 .138.112.25.25.25h8.5a.25.25 0 0 0 .25-.25V6H9.75A1.75 1.75 0 0 1 8 4.25V1.5Zm5.75.56v2.19c0 .138.112.25.25.25h2.19L9.5 2.06ZM2 1.75C2 .784 2.784 0 3.75 0h5.086c.464 0 .909.184 1.237.513l3.414 3.414c.329.328.513.773.513 1.237v8.086A1.75 1.75 0 0 1 12.25 15h-8.5A1.75 1.75 0 0 1 2 13.25Zm3.5 4a.75.75 0 0 0 0 1.5h5a.75.75 0 0 0 0-1.5h-5ZM5.5 10a.75.75 0 0 0 0 1.5h3a.75.75 0 0 0 0-1.5h-3Z" />
    </svg>
  );
}

interface ReferenceCardProps {
  reference: Reference;
  index: number;
}

export default function ReferenceCard({ reference, index }: ReferenceCardProps) {
  return (
    <div
      className={cn(
        "ghw-flex ghw-items-center ghw-gap-1.5 ghw-py-0.5",
        "ghw-animate-in ghw-fade-in ghw-slide-in-from-bottom-1 ghw-duration-200"
      )}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {/* Type icon */}
      {reference.result_type && (
        <TypeIcon type={reference.result_type} />
      )}

      {/* Repo + file */}
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={reference.permalink}
            target="_blank"
            rel="noopener noreferrer"
            className="ghw-text-[11px] ghw-text-foreground hover:ghw-text-primary ghw-transition-colors ghw-truncate ghw-min-w-0"
          >
            <span className="ghw-font-medium">{reference.repository}</span>
            {reference.result_type === "code" && (
              <span className="ghw-text-muted-foreground">
                /{reference.file_path.split("/").pop()}
              </span>
            )}
          </a>
        </TooltipTrigger>
        {reference.selected_reason && (
          <TooltipContent side="top" className="ghw-max-w-xs ghw-text-xs">
            {reference.selected_reason}
          </TooltipContent>
        )}
      </Tooltip>

      {/* Stars + license + time — right side */}
      <div className="ghw-flex ghw-items-center ghw-gap-1 ghw-shrink-0 ghw-ml-auto">
        {reference.stars != null && reference.stars > 0 && (
          <span className="ghw-text-[9px] ghw-text-muted-foreground ghw-flex ghw-items-center ghw-gap-0.5">
            <svg
              className="ghw-w-2.5 ghw-h-2.5"
              fill="currentColor"
              viewBox="0 0 16 16"
              aria-hidden="true"
            >
              <path d="M8 .25a.75.75 0 0 1 .673.418l1.882 3.815 4.21.612a.75.75 0 0 1 .416 1.279l-3.046 2.97.719 4.192a.75.75 0 0 1-1.088.791L8 12.347l-3.766 1.98a.75.75 0 0 1-1.088-.79l.72-4.194L.818 6.374a.75.75 0 0 1 .416-1.28l4.21-.611L7.327.668A.75.75 0 0 1 8 .25Z" />
            </svg>
            {reference.stars >= 1000
              ? (reference.stars / 1000).toFixed(1) + "k"
              : reference.stars}
          </span>
        )}
        {reference.license && (
          <span className="ghw-text-[8px] ghw-font-medium ghw-px-1 ghw-py-0.5 ghw-rounded ghw-bg-muted ghw-text-muted-foreground">
            {reference.license}
          </span>
        )}
        {reference.last_modified_at && (
          <span className="ghw-text-[9px] ghw-text-muted-foreground">
            {formatRelativeTime(reference.last_modified_at)}
          </span>
        )}
      </div>
    </div>
  );
}
